import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DataService } from '../services/dataService';
import { District, Location, Measurement, MeteoSchweizData, FilterState } from '../types';
import TemperatureTable from '../components/TemperatureTable';
import TemperaturePieChart from '../components/TemperaturePieChart';
import TemperatureDevelopmentChart from '../components/TemperatureDevelopmentChart';
import TemperatureDayOverview from '../components/TemperatureDayOverview';
import LocationComparison from '../components/LocationComparison';
import MultiDatePicker from '../components/MultiDatePicker';
import { Thermometer, MapPin, Sunrise, Sun, Moon } from 'lucide-react';
import { convertUTCToSwissTime, getSwissTimeComponents } from '../utils/timezoneUtils';



const DistrictDashboard: React.FC = () => {
  const { districtId } = useParams<{ districtId: string }>();
  const navigate = useNavigate();
  const [district, setDistrict] = useState<District | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [measurements, setMeasurements] = useState<Measurement[]>([]);
  const [measurementsWithUserComfort, setMeasurementsWithUserComfort] = useState<(Measurement & { user_comfort_answer?: string })[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  // const [temperatureStats, setTemperatureStats] = useState<TemperatureStats | null>(null); // No longer needed - using filtered stats
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'analysis' | 'comparison'>('overview');
  const [allDistricts, setAllDistricts] = useState<District[]>([]);
  const [meteoSchweizData, setMeteoSchweizData] = useState<MeteoSchweizData[]>([]);
  const [showMeteoSchweiz, setShowMeteoSchweiz] = useState(true);
  
  // Global filter state
  const [globalFilters, setGlobalFilters] = useState<FilterState>({
    selectedDates: [],
    timeOfDay: '',
    weatherConditions: [],
    generalHeatSensitivity: ''
  });

  useEffect(() => {
    const loadData = async () => {
      if (!districtId) return;

      try {
        setLoading(true);
        const [allDistrictsData, allLocations, allMeasurements, allMeasurementsWithUserComfort, allMeteoSchweizData] = await Promise.all([
          DataService.getDistricts(),
          DataService.getLocations(),
          DataService.getMeasurements(),
          DataService.getMeasurementsWithUserComfort(),
          DataService.getMeteoSchweizData(),
        ]);

        const districtIdNum = parseInt(districtId);
        
        // Handle special case for "Alle Gemeinden" (All Districts)
        if (districtIdNum === 0) {
          // Create a virtual district for "All Districts"
          const virtualDistrict: District = {
            id: 0,
            name: 'Alle Gemeinden'
          };
          
          // Use all locations and measurements
          const allDistrictMeasurements = allMeasurements;
          const allDistrictLocations = allLocations;
          
          setAllDistricts(allDistrictsData);
          setDistrict(virtualDistrict);
          setLocations(allDistrictLocations);
          setMeasurements(allDistrictMeasurements);
          setMeasurementsWithUserComfort(allMeasurementsWithUserComfort);
          setMeteoSchweizData(allMeteoSchweizData);
        } else {
          // Handle individual district
          const district = allDistrictsData.find(d => d.id === districtIdNum);
          if (!district) {
            navigate('/');
            return;
          }

          const districtLocations = allLocations.filter(l => l.district_id === districtIdNum);
          const districtMeasurements = allMeasurements.filter(m => m.district_id === districtIdNum);
          setAllDistricts(allDistrictsData);
          setDistrict(district);
          setLocations(districtLocations);
          setMeasurements(districtMeasurements);
          setMeasurementsWithUserComfort(allMeasurementsWithUserComfort.filter(m => m.district_id === districtIdNum));
          setMeteoSchweizData(allMeteoSchweizData);
        }
      } catch (error) {
        console.error('Error loading district data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [districtId, navigate]);

  const handleLocationChange = async (locationId: number | null) => {
    if (!locationId) {
      setSelectedLocation(null);
      return;
    }

    const location = locations.find(l => l.id === locationId) || null;
    setSelectedLocation(location);
  };

  // Global filtering logic
  const globallyFilteredMeasurements = useMemo(() => {
    let filtered = measurements;

    // Location filter (existing logic)
    if (selectedLocation) {
      filtered = filtered.filter(m => m.location_id === selectedLocation.id);
    }

    // Date filter - handle multiple dates (using Swiss time)
    if (globalFilters.selectedDates.length > 0) {
      filtered = filtered.filter(m => {
        const swissDate = convertUTCToSwissTime(m.created_at);
        
        // Check if the date is valid
        if (isNaN(swissDate.getTime())) {
          console.warn('Invalid date found:', m.created_at);
          return false;
        }
        
        const measurementDateString = swissDate.toISOString().split('T')[0];
        
        // Check if measurement date matches any of the selected dates
        return globalFilters.selectedDates.includes(measurementDateString);
      });
    }

    // Time of day filter - updated to match actual measurement windows (using Swiss time)
    if (globalFilters.timeOfDay) {
      filtered = filtered.filter(m => {
        const swissTimeComponents = getSwissTimeComponents(m.created_at);
        
        // Check if the date is valid
        if (isNaN(swissTimeComponents.hours) || isNaN(swissTimeComponents.minutes)) {
          console.warn('Invalid date found in time filter:', m.created_at);
          return false;
        }
        
        const hour = swissTimeComponents.hours;
        const minute = swissTimeComponents.minutes;
        const timeInMinutes = hour * 60 + minute;
        
        switch (globalFilters.timeOfDay) {
          case 'morning':
            // 6:30-8:30 Swiss time = 390-510 minutes (inclusive end time)
            return timeInMinutes >= 390 && timeInMinutes <= 510;
          case 'midday':
            // 12:00-13:30 Swiss time = 720-810 minutes (inclusive end time)
            return timeInMinutes >= 720 && timeInMinutes <= 810;
          case 'afternoon':
            // 16:30-18:30 Swiss time = 990-1110 minutes (inclusive end time)
            return timeInMinutes >= 990 && timeInMinutes <= 1110;
          default:
            return true;
        }
      });
    }

    // Weather conditions filter
    if (globalFilters.weatherConditions.length > 0) {
      filtered = filtered.filter(m => {
        return globalFilters.weatherConditions.some(condition => 
          m.weather_conditions && m.weather_conditions.some(wc => 
            wc.toLowerCase().includes(condition.toLowerCase())
          )
        );
      });
    }



    // General heat sensitivity filter
    if (globalFilters.generalHeatSensitivity) {
      // Filter by user's general heat sensitivity from profile
      filtered = filtered.filter(m => {
        const measurementWithComfort = measurementsWithUserComfort.find(mwc => mwc.id === m.id);
        return measurementWithComfort?.user_comfort_answer === globalFilters.generalHeatSensitivity;
      });
    }

    return filtered;
  }, [measurements, selectedLocation, globalFilters, measurementsWithUserComfort]);

  // Calculate temperature statistics from filtered measurements
  const filteredTemperatureStats = useMemo(() => {
    if (globallyFilteredMeasurements.length === 0) {
      return null;
    }


    // Group measurements by time of day using the same time windows as filters (Swiss time)
    const morningMeasurements = globallyFilteredMeasurements.filter(m => {
      const swissTimeComponents = getSwissTimeComponents(m.created_at);
      if (isNaN(swissTimeComponents.hours) || isNaN(swissTimeComponents.minutes)) return false;
      const hour = swissTimeComponents.hours;
      const minute = swissTimeComponents.minutes;
      const timeInMinutes = hour * 60 + minute;
      // Morning window: 6:30-8:30 Swiss time (same as filter)
      return timeInMinutes >= 390 && timeInMinutes <= 510;
    });

    const middayMeasurements = globallyFilteredMeasurements.filter(m => {
      const swissTimeComponents = getSwissTimeComponents(m.created_at);
      if (isNaN(swissTimeComponents.hours) || isNaN(swissTimeComponents.minutes)) return false;
      const hour = swissTimeComponents.hours;
      const minute = swissTimeComponents.minutes;
      const timeInMinutes = hour * 60 + minute;
      // Midday window: 12:00-13:30 Swiss time (same as filter)
      return timeInMinutes >= 720 && timeInMinutes <= 810;
    });

    const afternoonMeasurements = globallyFilteredMeasurements.filter(m => {
      const swissTimeComponents = getSwissTimeComponents(m.created_at);
      if (isNaN(swissTimeComponents.hours) || isNaN(swissTimeComponents.minutes)) return false;
      const hour = swissTimeComponents.hours;
      const minute = swissTimeComponents.minutes;
      const timeInMinutes = hour * 60 + minute;
      // Afternoon window: 16:30-18:30 Swiss time (same as filter)
      return timeInMinutes >= 990 && timeInMinutes <= 1110;
    });


    // Calculate statistics for each time period
    const calculateStats = (measurements: Measurement[]) => {
      const temps = measurements.map(m => m.measured_temperature).filter(t => t > 0);
      if (temps.length === 0) return { avg: 0, max: 0, min: 0 };
      
      return {
        avg: temps.reduce((a, b) => a + b, 0) / temps.length,
        max: Math.max(...temps),
        min: Math.min(...temps)
      };
    };

    const morningStats = calculateStats(morningMeasurements);
    const middayStats = calculateStats(middayMeasurements);
    const afternoonStats = calculateStats(afternoonMeasurements);

    return {
      measured: {
        morning: morningStats.avg,
        midday: middayStats.avg,
        afternoon: afternoonStats.avg
      },
      maxMeasured: {
        morning: morningStats.max,
        midday: middayStats.max,
        afternoon: afternoonStats.max
      },
      minMeasured: {
        morning: morningStats.min,
        midday: middayStats.min,
        afternoon: afternoonStats.min
      },
      perceived: {
        morning: 0, // We don't calculate perceived averages in this table
        midday: 0,
        afternoon: 0
      },
      maxPerceived: {
        morning: 0,
        midday: 0,
        afternoon: 0
      }
    };
  }, [globallyFilteredMeasurements]);


  const handleGlobalFilterChange = (filterType: keyof FilterState, value: any) => {
    setGlobalFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleDatesChange = (dates: string[]) => {
    setGlobalFilters(prev => ({
      ...prev,
      selectedDates: dates
    }));
  };

  const handleDistrictChange = (newDistrictId: number) => {
    // Navigate to the new district
    navigate(`/dashboard/${newDistrictId}`);
    
    // Force a page reload to ensure all data is properly updated
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-green-50 to-yellow-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Lade Dashboard...</p>
        </div>
      </div>
    );
  }

  if (!district) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-green-50 to-yellow-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Gemeinde nicht gefunden</p>
          <button 
            onClick={() => navigate('/')}
            className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            Zurück zur Übersicht
          </button>
        </div>
      </div>
    );
  }

  // Note: All filtering is now handled by globallyFilteredMeasurements

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-green-50 to-yellow-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Mobile Layout */}
          <div className="block md:hidden py-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <img 
                  src="/logo.png" 
                  alt="3-2-1 heiß! Logo" 
                  className="h-8 w-auto"
                />
                <div>
                  <h1 className="text-lg font-bold text-gray-900 truncate max-w-[150px]">{district.name}</h1>
                  <p className="text-xs text-gray-600">3-2-1 heiß!</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/')}
                className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-xs whitespace-nowrap"
              >
                Zurück
              </button>
            </div>
            {/* District Navigation - Mobile */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Gemeinde:</span>
              <select
                value={district.id}
                onChange={(e) => handleDistrictChange(parseInt(e.target.value))}
                className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value={0}>Alle Gemeinden</option>
                {allDistricts.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden md:flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <img 
                src="/logo.png" 
                alt="3-2-1 heiß! Logo" 
                className="h-10 w-auto"
              />
              <div>
                <h1 className="text-xl font-bold text-gray-900">{district.name}</h1>
                <p className="text-sm text-gray-600">3-2-1 heiß! Dashboard</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {/* District Navigation */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Gemeinde:</span>
                <select
                  value={district.id}
                  onChange={(e) => handleDistrictChange(parseInt(e.target.value))}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent min-w-[160px] max-w-[300px]"
                >
                  <option value={0}>Alle Gemeinden</option>
                  {allDistricts.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
              >
                Zurück zur Übersicht
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg p-6 shadow-md mb-8">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Filter & Einstellungen</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Standort
              </label>
              <select
                value={selectedLocation?.id || ''}
                onChange={(e) => handleLocationChange(e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">Alle Standorte</option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Datum</label>
              <MultiDatePicker
                selectedDates={globalFilters.selectedDates}
                onDatesChange={handleDatesChange}
                placeholder="Daten auswählen..."
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tageszeit</label>
              <select
                value={globalFilters.timeOfDay}
                onChange={(e) => handleGlobalFilterChange('timeOfDay', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">Alle Zeiten</option>
                <option value="morning">Morgens (6:30-8:30)</option>
                <option value="midday">Mittags (12:00-13:30)</option>
                <option value="afternoon">Nachmittags/Abends (16:30-18:30)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Wetterbedingungen</label>
              <select
                value={globalFilters.weatherConditions[0] || ''}
                onChange={(e) => handleGlobalFilterChange('weatherConditions', e.target.value ? [e.target.value] : [])}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">Alle Bedingungen</option>
                <option value="Die Sonne scheint (sehr starke Sonneneinstrahlung)">Sonne (starke Einstrahlung)</option>
                <option value="Die Sonne scheint, aber es hat vereinzelt Wolken">Sonne mit Wolken</option>
                <option value="Ein leichter Wind weht">Leichter Wind</option>
                <option value="Es ist stark bewölkt">Stark bewölkt</option>
                <option value="Es ist windstill">Windstill</option>
                <option value="Es regnet">Regen</option>
                <option value="Es hat vor weniger als zwei Stunden geregnet">Vor kurzem geregnet</option>
                <option value="Ein starker Wind weht">Starker Wind</option>
              </select>
            </div>



            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Gefühlte Hitze</label>
              <select
                value={globalFilters.generalHeatSensitivity}
                onChange={(e) => handleGlobalFilterChange('generalHeatSensitivity', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">Alle</option>
                <option value="nicht_wohl">Überhaupt nicht wohl</option>
                <option value="neutral">Weder wohl noch unwohl</option>
                <option value="wohl">Wohl</option>
              </select>
            </div>
          </div>
          
          {/* MeteoSchweiz Toggle */}
          <div className="mt-4 flex items-center space-x-3">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={showMeteoSchweiz}
                onChange={(e) => setShowMeteoSchweiz(e.target.checked)}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="text-sm font-medium text-gray-700">
                MeteoSchweiz Daten anzeigen
              </span>
            </label>
            <span className="text-xs text-gray-500">
              Offizielle Temperaturdaten für Vergleich
            </span>
          </div>
        </div>



        {/* Navigation Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-2">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6 overflow-x-auto scrollbar-hide">
              {(() => {
                const renderTabIcon = (iconType: string) => {
                  switch (iconType) {
                    case 'chart':
                      return (
                        <svg className="w-5 h-5" fill="none" stroke="#6ABD89" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      );
                    case 'thermometer':
                      return <Thermometer className="w-5 h-5" stroke="#6ABD89" />;
                    case 'map':
                      return <MapPin className="w-5 h-5" stroke="#6ABD89" />;
                    default:
                      return null;
                  }
                };

                return [
                  { id: 'overview', name: 'Übersicht', icon: 'chart' },
                  { id: 'analysis', name: 'Temperaturanalyse', icon: 'thermometer' },
                  { id: 'comparison', name: 'Standortvergleich', icon: 'map' }
                ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                                      {renderTabIcon(tab.icon)}
                  <span>{tab.name}</span>
                </button>
              ));
              })()}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Temperature Development Chart */}
            <TemperatureDevelopmentChart
              measurements={globallyFilteredMeasurements}
              meteoSchweizData={meteoSchweizData}
              showMeteoSchweiz={showMeteoSchweiz}
              title="Temperaturverlauf über den Tag"
            />

            {/* Pie Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <TemperaturePieChart
                measurements={globallyFilteredMeasurements}
                title="Verteilung gemessene Temperaturen (Temperaturbereiche)"
                type="measured"
              />
              <TemperaturePieChart
                measurements={globallyFilteredMeasurements}
                title="Verteilung gefühlte Temperaturen"
                type="perceived"
              />
            </div>
          </div>
        )}

        {activeTab === 'analysis' && (
          <div className="space-y-8">
            {/* Temperature Stats */}
            {filteredTemperatureStats && (
              <div className="grid grid-cols-1 gap-8">
                <TemperatureTable stats={filteredTemperatureStats} />
              </div>
            )}

            {/* Temperature Day Overview */}
            <TemperatureDayOverview
              measurements={globallyFilteredMeasurements}
              title="Temperaturverlauf über den Tag"
            />

            {/* Perceived Temperature Analysis */}
            <div className="bg-white rounded-lg p-6 shadow-md">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Gefühlte Temperatur Analyse</h3>
              
              {/* Perceived Temperature Distribution by Time of Day */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {['morning', 'midday', 'afternoon'].map((timeOfDay) => {
                  const timeLabel = {
                    morning: 'Morgens (6:30-8:30)',
                    midday: 'Mittags (12:00-13:30)', 
                    afternoon: 'Nachmittags/Abends (16:30-18:30)'
                  }[timeOfDay];
                  
                  
                  
                  const timeIcon = {
                    morning: <Sunrise className="w-5 h-5" stroke="#6ABD89" />,
                    midday: <Sun className="w-5 h-5" stroke="#6ABD89" />,
                    afternoon: <Moon className="w-5 h-5" stroke="#6ABD89" />
                  }[timeOfDay];
                  
                  // Filter measurements for this time period - updated to match actual measurement windows (Swiss time)
                  const timeMeasurements = globallyFilteredMeasurements.filter(measurement => {
                    const swissTimeComponents = getSwissTimeComponents(measurement.created_at);
                    const hour = swissTimeComponents.hours;
                    const minute = swissTimeComponents.minutes;
                    const timeInMinutes = hour * 60 + minute;
                    
                    switch (timeOfDay) {
                      case 'morning': return timeInMinutes >= 390 && timeInMinutes <= 510; // 6:30-8:30 Swiss time (inclusive end time)
                      case 'midday': return timeInMinutes >= 720 && timeInMinutes <= 810; // 12:00-13:30 Swiss time (inclusive end time)
                      case 'afternoon': return timeInMinutes >= 990 && timeInMinutes <= 1110; // 16:30-18:30 Swiss time (inclusive end time)
                      default: return false;
                    }
                  }).filter(m => m.perceived_temperature && m.perceived_temperature.trim() !== '');
                  
                  // Count perceived temperature categories
                  const perceivedCounts: { [key: string]: number } = {};
                  timeMeasurements.forEach(m => {
                    perceivedCounts[m.perceived_temperature] = (perceivedCounts[m.perceived_temperature] || 0) + 1;
                  });
                  
                  // Get most common perceived temperature
                  const mostCommon = Object.entries(perceivedCounts).length > 0
                    ? Object.entries(perceivedCounts).reduce((a, b) => a[1] > b[1] ? a : b)[0]
                    : 'Keine Daten';
                  
                  const getPerceivedColor = (perceived: string) => {
                    switch (perceived) {
                      case 'Sehr kalt': return '#284496';
                      case 'Kalt': return '#5d6ab0';
                      case 'Kühl': return '#9498cc';
                      case 'Leicht kühl': return '#cacbe7';
                      case 'Behaglich': return '#ffffff';
                      case 'Leicht warm': return '#f0cdbe';
                      case 'Warm': return '#e09a80';
                      case 'Heiss': return '#cf674a';
                      case 'Sehr heiss': return '#be331e';
                      default: return '#f3f4f6';
                    }
                  };
                  
                  return (
                    <div key={timeOfDay} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-3">
                        <span className="text-xl">{timeIcon}</span>
                        <h4 className="font-medium text-gray-800">{timeLabel}</h4>
                      </div>
                      
                      {/* Most Common Perceived Temperature */}
                      <div className="mb-4">
                        <p className="text-sm text-gray-600 mb-2">Häufigste gefühlte Temperatur:</p>
                        <div className="flex items-center space-x-2">
                          <div 
                            className="w-4 h-4 rounded-full border border-gray-300" 
                            style={{ backgroundColor: getPerceivedColor(mostCommon) }}
                          ></div>
                          <span className="font-medium text-gray-800">{mostCommon}</span>
                        </div>
                      </div>
                      
                      {/* Distribution */}
                      <div>
                        <p className="text-sm text-gray-600 mb-2">Verteilung:</p>
                        <div className="space-y-1">
                          {Object.entries(perceivedCounts)
                            .sort((a, b) => b[1] - a[1])
                            .slice(0, 3)
                            .map(([perceived, count]) => (
                              <div key={perceived} className="flex items-center justify-between text-xs">
                                <div className="flex items-center space-x-1">
                                  <div 
                                    className="w-3 h-3 rounded" 
                                    style={{ backgroundColor: getPerceivedColor(perceived) }}
                                  ></div>
                                  <span>{perceived}</span>
                                </div>
                                <span className="text-gray-600">{count}</span>
                              </div>
                            ))}
                        </div>
                      </div>
                      
                      <div className="mt-3 text-xs text-gray-500">
                        {timeMeasurements.length} Messungen
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

          </div>
        )}

        {activeTab === 'comparison' && (
          <LocationComparison
            locations={locations}
            measurements={globallyFilteredMeasurements}
            meteoSchweizData={meteoSchweizData}
            showMeteoSchweiz={showMeteoSchweiz}
            selectedLocation={selectedLocation}
          />
        )}
      </main>
    </div>
  );
};

export default DistrictDashboard;
